/* GDJ predictive autocomplete — self-hosted search engine (search.galeriedujouet.com).
 * Replaces the former Algolia autocomplete. Same DOM contract + .gdj-ac__* markup,
 * so the existing CSS is unchanged. Also fires personalization events. */
(function () {
  const panel = document.querySelector('[data-search-panel]');
  if (!panel) return;
  const input = panel.querySelector('[data-search-input]');
  const results = panel.querySelector('[data-search-results]');
  const { searchApiUrl, shop, locale, currency, customerId } = panel.dataset;
  if (!input || !results || !searchApiUrl || !shop) return;

  const API = searchApiUrl.replace(/\/$/, '');
  const LOCALE = (locale || 'fr').slice(0, 2);
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // First-party session id (anonymous personalization).
  const SID_KEY = 'gdj_search_session';
  let sessionId = localStorage.getItem(SID_KEY);
  if (!sessionId) {
    sessionId = 's_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(SID_KEY, sessionId);
  }

  let fmt;
  try { fmt = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: currency || 'EUR' }); } catch (_) { fmt = null; }
  function money(value) {
    const n = Number(value);
    if (!isFinite(n)) return '';
    return fmt ? fmt.format(n) : n.toFixed(2) + ' ' + (currency || '');
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }

  function sendEvents(events) {
    // Loi 25 / consent gate: personalization events only after explicit opt-in.
    if (!window.gdjConsent || window.gdjConsent.allowed() !== true) return;
    if (!events.length) return;
    const body = JSON.stringify({ shop, session_id: sessionId, customer_id: customerId || null, events });
    try {
      if (navigator.sendBeacon) { navigator.sendBeacon(`${API}/api/v1/events`, new Blob([body], { type: 'text/plain;charset=UTF-8' })); return; }
    } catch (_) { /* fall through */ }
    fetch(`${API}/api/v1/events`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  }

  function trackView(productId) {
    // Position in the dropdown and the list it belonged to, so typeahead clicks
    // join the same training set as the results page.
    sendEvents([{
      type: 'view',
      product_id: productId,
      query: recentQuery(),
      metadata: { position: acPos.get(String(productId)) || null, event_id: acImpressionId, surface: 'instant' },
    }]);
  }

  // --- Impressions for the dropdown ----------------------------------------
  let acImpressionId = '';
  const acPos = new Map();

  function trackAcImpressions(products, q) {
    acImpressionId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    acPos.clear();
    products.forEach((p, i) => acPos.set(String(p.id), i + 1));
    if (!products.length) return;
    if (!window.gdjConsent || window.gdjConsent.allowed() !== true) return;
    const body = JSON.stringify({
      shop, event_id: acImpressionId, session_id: sessionId, customer_id: customerId || null,
      surface: 'instant', q, page: 1, results_total: products.length,
      product_ids: products.map((p) => String(p.id)),
    });
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${API}/api/v1/impressions`, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
        return;
      }
    } catch (_) { /* fall through */ }
    fetch(`${API}/api/v1/impressions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  }

  // --- Which query led to a sale -------------------------------------------
  // Suggestions rank on what shoppers BOUGHT after searching, not just on what
  // they typed, so the query has to travel with the cart-add. It is kept for
  // the session only, and rides along solely on events consent already covers.
  const LASTQ_KEY = 'gdj_last_query';
  const QUERY_TTL_MS = 30 * 60 * 1000;   // a search stops explaining a purchase eventually

  function rememberQuery(q) {
    if (!q) return;
    try { sessionStorage.setItem(LASTQ_KEY, JSON.stringify({ q: String(q), at: Date.now() })); } catch (_) { /* private mode */ }
  }

  function recentQuery() {
    try {
      const v = JSON.parse(sessionStorage.getItem(LASTQ_KEY) || 'null');
      return v && v.q && Date.now() - v.at < QUERY_TTL_MS ? v.q : '';
    } catch (_) { return ''; }
  }

  // Landing on a results page IS the search, however the shopper got there.
  try {
    const urlQ = new URLSearchParams(location.search).get('q');
    if (urlQ) rememberQuery(urlQ);
  } catch (_) { /* no URLSearchParams: skip */ }

  // Shopify's AJAX cart is the one event every add path goes through — quick
  // add, product form, upsell — so wrapping it beats hooking each of them.
  const origFetch = window.fetch;
  if (typeof origFetch === 'function') {
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const promise = origFetch.apply(this, arguments);
      if (!/\/cart\/add(\.js)?(\?|$)/.test(url)) return promise;
      return promise.then((res) => {
        try {
          res.clone().json().then(trackCartAdd).catch(() => {});
        } catch (_) { /* opaque response: nothing to read */ }
        return res;
      });
    };
  }

  function trackCartAdd(data) {
    const q = recentQuery();
    if (!q || !data) return;               // no search behind it: nothing to credit
    const items = Array.isArray(data.items) ? data.items : [data];
    const events = items
      .map((i) => i && i.product_id)
      .filter(Boolean)
      .slice(0, 5)
      .map((id) => ({
        type: 'add_to_cart',
        product_id: String(id).startsWith('gid://') ? String(id) : `gid://shopify/Product/${id}`,
        query: q,
      }));
    sendEvents(events);
  }

  let timer = null;
  let lastQuery = '';

  // -------- Empty-state history panel: recent searches / last product / last
  // category, shown when the field is focused and empty. FR labels with an EN
  // fallback (v1; no locale-file plumbing yet). --------
  const IS_EN = (document.documentElement.lang || '').toLowerCase().startsWith('en');
  const T = {
    recentSearches: IS_EN ? 'My recent searches' : 'Mes dernières recherches',
    lastProduct: IS_EN ? 'Last viewed product' : 'Dernier produit consulté',
    lastCategory: IS_EN ? 'Last visited category' : 'Dernière catégorie visitée',
    inCategory: IS_EN ? 'in' : 'dans',
    category: IS_EN ? 'Category' : 'Catégorie',
    brand: IS_EN ? 'Brand' : 'Marque',
    products: IS_EN ? 'Products' : 'Produits',
  };

  function readJson(key, fallback) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return fallback;
      const parsed = JSON.parse(raw);
      return parsed == null ? fallback : parsed;
    } catch (_) { return fallback; }
  }

  /** Record a submitted query: last 5 distinct (case-insensitive), newest first. */
  function writeRecentSearch(q) {
    try {
      q = String(q || '').trim();
      if (!q) return;
      const KEY = 'gdj_recent_searches';
      let list = readJson(KEY, []);
      if (!Array.isArray(list)) list = [];
      const lower = q.toLowerCase();
      list = list.filter((item) => item && String(item.q || '').toLowerCase() !== lower);
      list.unshift({ q, t: Date.now() });
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, 5)));
    } catch (_) { /* storage unavailable: history is a nicety, not required */ }
  }

  // History entries keep the URL of the language they were visited in
  // (/en/collections/...). Handles are shared by FR and EN, so re-root the
  // path on the current language: a French shopper who once browsed in English
  // must not be bounced to the English site by « Dernière catégorie visitée ».
  // Done at render time so links already saved in browsers are fixed too.
  function localPath(url) {
    try {
      const u = new URL(url, location.origin);
      if (u.origin !== location.origin) return url;
      const path = u.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/(?:collections|products|pages|blogs|search)(?:\/|$))/i, '');
      if (!/^\/(?:collections|products|pages|blogs|search)(?:\/|$)/.test(path)) return url;
      return root + path.replace(/^\//, '') + u.search;
    } catch (_) { return url; }
  }

  function renderHistory() {
    const searches = readJson('gdj_recent_searches', []);
    const lastProduct = readJson('gdj_last_product', null);
    const lastCategory = readJson('gdj_last_category', null);

    let html = '';

    if (Array.isArray(searches) && searches.length) {
      const chips = searches.slice(0, 5)
        .map((item) => (item && item.q ? String(item.q) : ''))
        .filter(Boolean)
        .map((q) => `<a class="gdj-ac__suggest" href="${root}search?q=${encodeURIComponent(q)}&type=product">${escapeHtml(q)}</a>`)
        .join('');
      if (chips) {
        html += `<div class="gdj-ac__hist-section"><p class="gdj-ac__hist-title">${escapeHtml(T.recentSearches)}</p><div class="gdj-ac__suggests">${chips}</div></div>`;
      }
    }

    if (lastProduct && lastProduct.url && lastProduct.title) {
      const img = lastProduct.image
        ? `<img class="gdj-ac__img" src="${escapeHtml(lastProduct.image)}" alt="" loading="lazy" width="48" height="48">`
        : '<span class="gdj-ac__img"></span>';
      html += `<div class="gdj-ac__hist-section"><p class="gdj-ac__hist-title">${escapeHtml(T.lastProduct)}</p>`
        + `<a class="gdj-ac__item" href="${escapeHtml(localPath(lastProduct.url))}">${img}<span class="gdj-ac__name">${escapeHtml(lastProduct.title)}</span></a></div>`;
    }

    if (lastCategory && lastCategory.url && lastCategory.title) {
      html += `<div class="gdj-ac__hist-section"><p class="gdj-ac__hist-title">${escapeHtml(T.lastCategory)}</p>`
        + `<a class="gdj-ac__item" href="${escapeHtml(localPath(lastCategory.url))}"><span class="gdj-ac__name">${escapeHtml(lastCategory.title)}</span></a></div>`;
    }

    if (!html) { results.hidden = true; results.innerHTML = ''; return; }
    results.innerHTML = `<div class="gdj-ac__hist">${html}</div>`;
    results.hidden = false;
  }

  async function query(q) {
    try {
      const url = `${API}/api/v1/instant-search?q=${encodeURIComponent(q)}&shop=${encodeURIComponent(shop)}&locale=${LOCALE}&session_id=${encodeURIComponent(sessionId)}${customerId ? `&customer_id=${encodeURIComponent(customerId)}` : ''}`;
      const res = await fetch(url);
      if (!res.ok) return;
      const data = await res.json();
      if (q !== lastQuery) return; // stale response
      render(data, q);
    } catch (_) {
      /* network error: native form submit still works */
    }
  }

  const fold = (v) => String(v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

  /**
   * How many characters of `text` the shopper has already typed, so the rest
   * can be bolded the way Amazon does it. Folding can change length (accents,
   * punctuation), so the boundary is probed around the typed length rather than
   * assumed; 0 means "don't bold anything", never a wrong split.
   */
  function typedLen(text, q) {
    const fq = fold(q).trim();
    if (!fq) return 0;
    for (let n = Math.max(1, fq.length - 2); n <= Math.min(text.length, fq.length + 3); n++) {
      if (fold(text.slice(0, n)).trim() === fq) return n;
    }
    return 0;
  }

  function highlighted(text, q) {
    const n = typedLen(text, q);
    if (!n) return escapeHtml(text);
    return escapeHtml(text.slice(0, n)) + '<b>' + escapeHtml(text.slice(n)) + '</b>';
  }

  function suggestUrl(s) {
    // (clicks are remembered by the delegated handler below)
    if ((s.type === 'category' || s.type === 'brand') && s.handle) {
      return `${root}collections/${encodeURIComponent(s.handle)}`;
    }
    const scoped = s.scope ? `&ptype=${encodeURIComponent(s.scope)}` : '';
    return `${root}search?q=${encodeURIComponent(s.text)}&type=product${scoped}`;
  }

  // A row says three things: what will be searched (with the completion bold),
  // what kind of thing it is, and — for the top term — which category it will
  // search in. Without the last two a shopper cannot tell a category from a
  // query, which was the whole complaint.
  function suggestRow(s, q) {
    const tag = s.type === 'category' ? T.category : s.type === 'brand' ? T.brand : '';
    const scope = s.scope
      ? `<span class="gdj-ac__sug-scope">${escapeHtml(T.inCategory)} ${escapeHtml(s.scope)}</span>`
      : '';
    return `<a class="gdj-ac__sug gdj-ac__sug--${s.type}" href="${suggestUrl(s)}">
      <span class="gdj-ac__sug-icon" aria-hidden="true">${s.type === 'term' ? '&#9906;' : '&#9656;'}</span>
      <span class="gdj-ac__sug-text">${highlighted(s.text, q)}${scope}</span>
      ${tag ? `<span class="gdj-ac__sug-tag">${escapeHtml(tag)}</span>` : ''}
    </a>`;
  }

  function render(data, q) {
    // Rich rows when the engine sends them; the old string list still renders
    // if a cached response predates the upgrade.
    const rich = Array.isArray(data.suggest) && data.suggest.length
      ? data.suggest
      : (data.suggestions || []).map((t) => ({ text: t, type: 'term' }));
    // A term identical to what was typed adds nothing; a category with that
    // name is still somewhere to go.
    const rows = rich.filter((s) => s && s.text && (s.type !== 'term' || fold(s.text) !== fold(q)));
    // Three is what a shopper scans without losing the suggestions above them.
    const products = (data.products || []).slice(0, 3);

    if (!products.length && !rows.length) {
      results.innerHTML = `<p class="gdj-ac__empty">${escapeHtml(results.dataset.emptyText || 'Aucun résultat')}</p>`;
      results.hidden = false;
      return;
    }

    const suggestHtml = rows.length
      ? `<div class="gdj-ac__suggests">${rows.map((s) => suggestRow(s, q)).join('')}</div>`
      : '';
    const items = products.map((p) => {
      const imgUrl = p.image;
      const img = imgUrl ? `<img class="gdj-ac__img" src="${escapeHtml(imgUrl)}" alt="" loading="lazy" width="48" height="48">` : '<span class="gdj-ac__img"></span>';
      const sale = p.special_price != null && p.regular_price != null && Number(p.special_price) < Number(p.regular_price);
      // Match the other renderers (search-collection, related, favorites): the
      // price sync keeps regular_price/special_price fresh but never rewrites the
      // variants JSON, so p.price is stale for non-sale items. Fall back to
      // regular_price, and only then to the raw variant price.
      const shown = p.special_price != null ? p.special_price
        : (p.regular_price != null ? p.regular_price : p.price);
      const price = shown != null
        ? `<span class="gdj-ac__price${sale ? ' gdj-ac__price--sale' : ''}">${money(shown)}</span>`
        : '';
      const pid = escapeHtml(p.id);
      return `<a class="gdj-ac__item" href="${root}products/${escapeHtml(p.handle)}" data-gdj-ac-id="${pid}">
        ${img}
        <span class="gdj-ac__name">${highlighted(p.title, q)}</span>
        ${price}
      </a>`;
    }).join('');
    const head = products.length ? `<p class="gdj-ac__hist-title">${escapeHtml(T.products)}</p>` : '';
    const all = products.length
      ? `<a class="gdj-ac__all" href="${root}search?q=${encodeURIComponent(q)}&type=product">${escapeHtml(results.dataset.allText || 'Voir tous les résultats')}</a>`
      : '';
    results.innerHTML = suggestHtml + head + items + all;
    results.hidden = false;
    trackAcImpressions(products, q);
    results.querySelectorAll('[data-gdj-ac-id]').forEach((a) => {
      a.addEventListener('click', () => trackView(a.getAttribute('data-gdj-ac-id')));
    });
    results.querySelectorAll('.gdj-ac__sug').forEach((a) => {
      const row = rows[[...results.querySelectorAll('.gdj-ac__sug')].indexOf(a)];
      a.addEventListener('click', () => rememberQuery(row ? row.text : q));
    });
  }

  input.addEventListener('input', function () {
    const q = input.value.trim();
    lastQuery = q;
    clearTimeout(timer);
    if (!q) { renderHistory(); return; }
    if (q.length < 2) { results.hidden = true; results.innerHTML = ''; return; }
    timer = setTimeout(() => query(q), 160);
  });

  // Empty + focused: show the history panel (recent searches / last product /
  // last category) instead of leaving the field with nothing underneath it.
  input.addEventListener('focus', function () {
    if (!input.value.trim()) renderHistory();
  });

  // Record the submitted query (Enter or the form's implicit submit) for the
  // "Mes dernières recherches" panel. The /search results page itself also
  // records it (gdj-search-collection.js) so a query typed via a direct link
  // or browser navigation is captured too.
  const form = panel.querySelector('form');
  if (form) {
    form.addEventListener('submit', function () {
      writeRecentSearch(input.value);
      rememberQuery(input.value);   // credit this query if it ends in a cart add
    });
  }

  // Hide suggestions when clicking outside the panel.
  document.addEventListener('click', function (e) {
    if (!panel.contains(e.target)) results.hidden = true;
  });
})();
